import { useMemo, useState } from 'react';
import { useEditor } from '../../state/store';
import { Animation } from '../../animation/model';
import {
  AnimationExportOptions,
  DEFAULT_ANIMATION_EXPORT,
  exportAnimation,
  toAsciicast,
  toPlayerScript,
  toSingleStream,
} from '../../animation/export';
import { formatBytes } from '../../ansi/Optimizer';
import { escapeForDisplay } from '../../ansi/SGR';
import { ColorMode } from '../../core/types';
import { copyToClipboard, downloadText } from '../fileIO';
import { Checkbox, Field, Modal, Segmented } from '../widgets/common';
import { Select } from '../widgets/primitives';

type Target = 'stream' | 'cast' | 'player' | 'frames';

export function AnimationExportDialog({
  animation,
  onClose,
}: {
  animation: Animation;
  onClose: () => void;
}): JSX.Element {
  const store = useEditor.getState();
  const tab = store.active();
  const [target, setTarget] = useState<Target>('player');
  const [opts, setOpts] = useState<AnimationExportOptions>({
    ...DEFAULT_ANIMATION_EXPORT,
    colorMode: tab.doc.colorMode,
    background: tab.doc.background,
  });

  const exported = useMemo(
    () => exportAnimation(tab.doc, animation, opts),
    [tab.doc, animation, opts],
  );

  const { text, extension } = useMemo(() => {
    switch (target) {
      case 'stream':
        return { text: toSingleStream(exported, opts), extension: 'ans' };
      case 'cast':
        return {
          text: toAsciicast(exported, tab.doc.width, tab.doc.height, animation.name),
          extension: 'cast',
        };
      case 'frames':
        return {
          text: exported.frames
            .map((f) => `# ${f.name} (${f.durationMs}ms, ${f.bytes} B)\n${f.ansi}`)
            .join('\n\u001a\n'),
          extension: 'frames.ans',
        };
      default:
        return { text: toPlayerScript(exported, animation, tab.doc.name), extension: 'player.ts' };
    }
  }, [target, exported, opts, animation, tab.doc]);

  const fileName = `${(tab.doc.name || 'animation').trim()}.${extension}`;

  return (
    <Modal
      title={`Export "${animation.name}"`}
      onClose={onClose}
      width={800}
      footer={
        <>
          <span className="faint mono" style={{ marginRight: 'auto' }}>
            {exported.frames.length} frames · {formatBytes(exported.totalBytes)} ·{' '}
            {formatBytes(exported.bytesPerSecond)}/s
          </span>
          <button
            className="btn"
            onClick={async () => {
              const ok = await copyToClipboard(text);
              store.notify(ok ? 'Copied.' : 'Clipboard access was blocked.', ok ? 'success' : 'warn');
            }}
          >
            Copy
          </button>
          <button className="btn" onClick={onClose}>
            Close
          </button>
          <button
            className="btn primary"
            onClick={() => {
              downloadText(fileName, text);
              store.notify(`Exported ${fileName}`, 'success');
            }}
          >
            Download {fileName}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 14 }}>
        <div>
          <Field label="Target">
            <Select
              ariaLabel="Target"
              value={target}
              options={[
                { value: 'player' as Target, label: 'TypeScript player', hint: 'runs in a terminal' },
                { value: 'stream' as Target, label: 'Single ANSI stream', hint: 'one file, replayed' },
                { value: 'cast' as Target, label: 'asciinema .cast', hint: 'share on the web' },
                { value: 'frames' as Target, label: 'Separate frames', hint: 'one file per frame' },
              ]}
              onChange={setTarget}
            />
          </Field>

          <Field label="Colour">
            <Segmented
              value={opts.colorMode}
              options={[
                { value: 'ansi16' as ColorMode, label: '16' },
                { value: 'ansi256' as ColorMode, label: '256' },
                { value: 'truecolor' as ColorMode, label: 'True' },
              ]}
              onChange={(v) => setOpts({ ...opts, colorMode: v })}
            />
          </Field>

          <Checkbox
            label="Differential frames"
            checked={opts.differential}
            onChange={(v) => setOpts({ ...opts, differential: v })}
            title="After the first frame, repaint only the cells that changed"
          />
          <Checkbox
            label="Clear screen first"
            checked={opts.clearFirst}
            onChange={(v) => setOpts({ ...opts, clearFirst: v })}
          />
          <Checkbox
            label="Hide cursor during playback"
            checked={opts.hideCursor}
            onChange={(v) => setOpts({ ...opts, hideCursor: v })}
          />

          <div style={{ marginTop: 10, fontSize: 11 }} className="dim">
            <div>First frame: {formatBytes(exported.frames[0]?.bytes ?? 0)}</div>
            <div>Average: {formatBytes(exported.averageFrameBytes)}</div>
            <div>Peak: {formatBytes(exported.peakFrameBytes)}</div>
            {opts.differential ? (
              <div className="ok-text">
                Changed cells per frame:{' '}
                {exported.frames
                  .slice(1)
                  .map((f) => f.changedCells)
                  .join(', ') || '—'}
              </div>
            ) : null}
            {exported.warning ? (
              <div className="warn-text" style={{ lineHeight: 1.45, marginTop: 4 }}>
                ⚠ {exported.warning}
              </div>
            ) : null}
          </div>
        </div>

        <div>
          <div className="faint" style={{ marginBottom: 4 }}>
            Preview
          </div>
          <pre className="terminal-preview" style={{ maxHeight: 430, fontSize: 10 }}>
            {(target === 'player' || target === 'cast' ? text : escapeForDisplay(text)).slice(0, 20000)}
          </pre>
        </div>
      </div>
    </Modal>
  );
}
